import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, type LucideIcon, Sparkles } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ZodType } from 'zod';
import { type AiSuggestionView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { aiApi, aiKeys } from '../api';
import { useTrackedAiRun } from '../use-ai-run';
import { AiAction } from './ai-action';
import { skillAvailability, useAiCapabilities } from '../use-ai-capabilities';
import { PartialSuggestionNotice } from './partial-suggestion-notice';

/**
 * Khung chung cho **một tính năng AI trên một trang nghiệp vụ** — APM-AI-nut.
 *
 * ## Vì sao một khung, không phải chín bản chép tay
 *
 * Chín tính năng AI đã có skill chạy được ở backend nhưng chưa có nút trên
 * trang nào. Mỗi cái cần đúng bốn thứ giống hệt nhau: nút chạy (hoặc đường làm
 * tay khi chưa bật AI), theo dõi lượt chạy, đọc lại gợi ý đã lưu sau khi tải
 * lại trang, và ba cảnh báo của harness (`partial`, `unverified`, chi phí).
 *
 * Chép tay chín lần thì chúng trôi khỏi nhau ở đúng chỗ khó thấy nhất — chỗ
 * **cảnh báo**. Một màn hình quên `PartialSuggestionNotice` sẽ hiện một kết quả
 * dựng từ dữ liệu chưa đọc hết y như một kết quả đầy đủ, và không gì nói ra.
 *
 * Phần **khác nhau** giữa chín tính năng chỉ là hình dạng nội dung, nên nó là
 * `render` — một hàm, nhận nội dung đã qua Zod.
 *
 * ## Vì sao đọc lại gợi ý đã lưu, không chỉ giữ kết quả trong state
 *
 * Mọi output AI ghi vào `ai_suggestion` (ADR-005). Chỉ giữ trong state thì F5
 * là mất, và người dùng chạy lại — tốn thêm một lượt gọi model cho thứ đã có
 * sẵn trong CSDL. Kết quả vừa chạy vẫn hiện ngay từ phản hồi HTTP, không chờ
 * truy vấn làm mới.
 */
export function AiSkillPanel<T>({
  skill,
  projectId,
  title,
  help,
  icon: Icon = Sparkles,
  label,
  prompt,
  params,
  schema,
  canRun = true,
  routeParams,
  controls,
  render,
  actions,
  defaultOpen = false,
  offlineMode = 'manual',
  tour,
  select,
}: {
  readonly skill: string;
  readonly projectId: string;
  readonly title: string;
  /** Một dòng nói tính năng này trả lời câu hỏi gì. */
  /** Chuỗi thì hiện thành một đoạn; cần gạch đầu dòng thì truyền phần tử React. */
  readonly help?: ReactNode;
  readonly icon?: LucideIcon;
  readonly label: string;
  readonly prompt: string;
  readonly params?: Record<string, unknown>;
  /** Schema của `content` — cùng schema mà skill khai ở `output`. */
  readonly schema: ZodType<T>;
  /** Tham số chưa đủ thì nút tắt (ví dụ ô câu hỏi còn trống). */
  readonly canRun?: boolean;
  readonly routeParams?: Record<string, string>;
  /** Ô nhập tham số, vẽ phía trên nút. */
  readonly controls?: ReactNode;
  readonly render: (content: T, suggestion: AiSuggestionView) => ReactNode;
  /** Hành động áp dụng kết quả — chỉ cho tính năng thật sự ghi được gì đó. */
  readonly actions?: (content: T, suggestion: AiSuggestionView) => ReactNode;
  readonly defaultOpen?: boolean;
  /**
   * Nói gì khi dự án **chưa cấu hình AI** — APM-PSET-k.
   *
   * - `manual` (mặc định): chỉ sang đường làm tay của skill. Đúng cho những
   *   khối mà việc ấy làm tay được thật.
   * - `needKey`: nói thẳng "chưa có key AI, thêm key để dùng". Đúng cho những
   *   khối mà **không có** đường làm tay tương đương — đọc lại toàn bộ RAID,
   *   bình luận và tài liệu để dựng lại bối cảnh một quyết định cũ thì câu
   *   "lọc RAID theo loại Quyết định" không phải là cùng một việc, và chỉ sang
   *   đó là hứa một thứ mình không làm được.
   */
  readonly offlineMode?: 'manual' | 'needKey';
  /**
   * Neo `data-tour` của khối — tour AI chiếu sáng thẳng vào nó (APM-TOUR-ai).
   * Gắn trên khung, không trên nút chạy: khung có mặt cả khi AI chưa bật (nó
   * hiện đường làm tay), còn nút thì không.
   */
  readonly tour?: string;
  /**
   * Chỉ gợi ý **của đúng đối tượng này** — APM-GIT-n. `GET /ai/suggestions` trả gợi
   * ý của cả dự án, nên một khung đặt trên một việc mà chỉ lọc theo `skill` sẽ hiện
   * kết quả của việc khác sau F5. Bỏ trống = mọi gợi ý cùng skill (như trước).
   */
  readonly select?: (suggestion: AiSuggestionView) => boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const track = useTrackedAiRun();
  const [open, setOpen] = useState(defaultOpen);
  /** Kết quả của lượt vừa chạy — hiện ngay, không chờ truy vấn làm mới. */
  const [fresh, setFresh] = useState<AiSuggestionView | null>(null);

  const suggestions = useQuery({
    queryKey: aiKeys.suggestions(projectId),
    queryFn: () => aiApi.suggestions(projectId),
    // Chỉ tải khi mục được mở: chín khung này nằm rải khắp các tab, và tải hết
    // lúc mở dự án là chín lượt gọi cho thứ chưa ai nhìn.
    enabled: open,
  });

  const run = useMutation({
    mutationFn: () =>
      track({ skill, label: title }, (runId) =>
        aiApi.runSkill(skill, { projectId, prompt, params, runId }),
      ),
    onSuccess: (result) => {
      setFresh(result);
      void queryClient.invalidateQueries({ queryKey: aiKeys.suggestions(projectId) });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => aiApi.review(id, 'reject'),
    onSuccess: () => {
      setFresh(null);
      void queryClient.invalidateQueries({ queryKey: aiKeys.suggestions(projectId) });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  /*
    Gợi ý đang hiện: ưu tiên lượt vừa chạy, sau đó tới bản mới nhất còn chờ.

    Lọc `pending` chứ không lấy bản mới nhất bất kể trạng thái: một gợi ý đã bị
    bỏ qua mà vẫn hiện lại sau F5 thì nút "Bỏ qua" đọc như không có tác dụng.
  */
  const stored = (suggestions.data ?? [])
    .filter((item) => item.skill === skill && item.status === 'pending' && (select?.(item) ?? true))
    .at(0);
  const current = fresh ?? stored ?? null;

  /*
    Chưa cấu hình AI **và** khối này không có đường làm tay tương đương — xem
    `offlineMode`. Hỏi `useAiCapabilities` ở đây chứ không để `AiAction` tự lo:
    câu trả lời quyết định **thay** cả cái nút, không chỉ nội dung bên trong nó.
  */
  const capabilities = useAiCapabilities();
  const needKey =
    offlineMode === 'needKey' && !skillAvailability(skill, capabilities.data).available;
  const parsed = current ? schema.safeParse(current.content) : null;

  return (
    <section data-tour={tour} className="rounded-DEFAULT border border-border">
      <button
        type="button"
        className="flex w-full items-center gap-2 p-2 text-left text-sm font-medium"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? (
          <ChevronDown aria-hidden className="size-4" />
        ) : (
          <ChevronRight aria-hidden className="size-4" />
        )}
        <Icon aria-hidden className="size-4 text-muted-foreground" />
        {title}
        {current ? (
          <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs">
            {t('aiPanel.hasResult')}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-3 border-t border-border p-3">
          {typeof help === 'string' ? (
            <p className="text-xs text-muted-foreground">{help}</p>
          ) : help ? (
            <div className="text-xs text-muted-foreground">{help}</div>
          ) : null}

          {controls}

          <div className="flex flex-wrap items-center gap-2">
            {/*
              `showManual`: khối này **nói về chính tính năng AI**, nên khi chưa
              bật AI nó phải giải thích và chỉ sang đường làm tay. Một khối
              trống không lời nào còn tệ hơn một câu nhắc.

              Khác hẳn `AiAction` trên thanh công cụ Công việc hay thanh Replay:
              ở đó câu nhắc là nhiễu, và việc cấu hình key đã gom vào badge đỏ
              trên tab Cấu hình.
            */}
            {/*
              Chưa có key thì nút **vẫn hiện** (2026-09-25): bấm vào là hộp "Cần
              key AI" của chính `AiAction`, thay cho dòng chữ đứng một mình.
            */}
            <AiAction
              skill={skill}
              label={label}
              showManual
              pending={run.isPending}
              // Tắt hẳn khi thiếu tham số: mở hộp "Thực thi" rồi bấm vào
              // không có gì xảy ra là một lời hứa hụt.
              disabled={!canRun}
              onRun={() => run.mutate()}
              {...(routeParams ? { routeParams } : {})}
            />
            {/*
              Chưa có key thì **không** nhắc điền tham số: người dùng không
              thiếu một ô nhập, họ thiếu một cái key — và hai câu nhắc cạnh nhau
              làm câu quan trọng hơn trông như một chú thích.
            */}
            {!canRun && !needKey ? (
              <span className="text-xs text-muted-foreground">{t('aiPanel.needParams')}</span>
            ) : null}
          </div>

          {suggestions.isPending && !fresh ? <Skeleton className="h-16 w-full" /> : null}
          {suggestions.isError && !fresh ? (
            <ErrorState
              message={errorMessage(suggestions.error, t)}
              onRetry={() => void suggestions.refetch()}
            />
          ) : null}

          {current ? (
            <div className="flex flex-col gap-2 rounded-DEFAULT bg-muted/40 p-3">
              <PartialSuggestionNotice suggestion={current} />

              {parsed?.success ? (
                render(parsed.data, current)
              ) : (
                /*
                  Nội dung không khớp schema là chuyện **có thể xảy ra**: gợi ý
                  cũ sinh ra trước một lần đổi schema vẫn nằm trong CSDL. Nói ra
                  thay vì vẽ một khối trống — một khối trống đọc như tính năng
                  hỏng.
                */
                <p className="text-xs text-rag-amber">{t('aiPanel.contentMismatch')}</p>
              )}

              {current.rationale ? (
                <p className="text-xs text-muted-foreground">{current.rationale}</p>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
                {parsed?.success && actions ? actions(parsed.data, current) : null}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={dismiss.isPending}
                  onClick={() => dismiss.mutate(current.id)}
                >
                  {t('aiPanel.dismiss')}
                </Button>
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {t('aiPanel.cost', { value: current.costUsd })}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
